import { useSelector, useDispatch } from 'react-redux';
import TennisUser from '../tennisUser';
import './index.scss';
import { useNavigate } from 'react-router-dom';
import { getTennisOrderAPI } from '../../apis/auction';
import { useEffect, useState } from 'react';
import { Card, List, Pagination, Switch, Button, message, Image } from 'antd';
import logo from '../../assets/logo.png';
import OrderTennisStatusTag from '../OrderTennisStatusTag';
import { addCart, addComboToCart } from '../../store/modules/takeaway';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

const TennisPurchaseHistory = () => {
  const { t } = useTranslation();
  const { userInfo } = useSelector((state: any) => state.user);
  const { cartList } = useSelector((state: any) => state.foods);
  const dispatch = useDispatch();
  // dispatch(removeCartItem({ itemId }));
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(4);
  const [visible, setVisible] = useState({});
  const handlePageChange = (page, pageSize) => {
    setCurrentPage(page);
    setPageSize(pageSize);
  };
  const handleLogoClick = () => {
    navigate('/');
  };

  // 更稳健地根据 ctime（MM-DD HH:mm）和 oid 提取年份来排序
  const parseOrderDate = (order: any) => {
    // 尝试从 oid 提取年份（oid 形如 202512230001）
    const oidStr = String(order.oid || '');
    const year = oidStr.length >= 4 ? parseInt(oidStr.slice(0, 4), 10) : new Date().getFullYear();

    // 匹配 "MM-DD HH:mm" 或 "MM-DD HH: mm" 等格式
    const m = String(order.ctime || '').match(/(\d{2})-(\d{2})\s+(\d{1,2}):\s*(\d{2})/);
    if (m) {
      const month = parseInt(m[1], 10);
      const day = parseInt(m[2], 10);
      const hour = parseInt(m[3], 10);
      const minute = parseInt(m[4], 10);
      return new Date(year, month - 1, day, hour, minute);
    }

    // 兜底：尝试标准解析，失败时返回极早的时间
    const d = new Date(order.ctime);
    return isNaN(d.getTime()) ? new Date(0) : d;
  };

  const paginatedOrders = [...orders]
    .sort((a, b) => parseOrderDate(b).getTime() - parseOrderDate(a).getTime())
    .slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const handleToggleVisibility = (oid) => {
    setVisible((prevVisible) => ({
      ...prevVisible,
      [oid]: !prevVisible[oid],
    }));
  };


  useEffect(() => {
    const fetchOrders = async () => {
      try {
        if (!userInfo.userId) return;
        console.log('Fetching tennis orders for buyerId:', userInfo.userId);
        // 用 buyerId 查询该用户的网球订单
        const resp = await getTennisOrderAPI({ buyerId: userInfo.userId });
        console.log('getTennisOrderAPI response:', resp);
        if (resp && resp.success && Array.isArray(resp.orders)) {
          const normalized = resp.orders.map((o: any) => ({
            ...o,
            oid: o.id ?? o.oid,
            amount: o.price ?? o.amount,
            ctime: o.created_at ?? o.ctime,
            orderFoodItems: o.orderFoodItems ?? [],
            tableNumber: o.tableNumber ?? null,
          }));

          setOrders(normalized);
          // 初始化 visible 状态，使所有订单的内容都显示
          const initialVisibleState = normalized.reduce((acc: any, order: any) => {
            acc[order.oid] = true;
            return acc;
          }, {});
          setVisible(initialVisibleState);
        } else {
          setOrders([]);
        }
      } catch (error) {
        console.error('Error fetching tennis orders by buyerId:', error);
      }
    };

    if (userInfo.userId) {
      fetchOrders();
    }
  }, [userInfo.userId]);

  return (
    <div className="purchaseHistoryContainer">
      <div className="scrollArea">
        <div className="header">
          <div className="backToTop">
            <img
              className="login-logo"
              src={logo}
              alt=""
              onClick={handleLogoClick}
            />
          </div>
          <span className="text">{t('purchaseHistory.title')}</span>
          <div className="user-info">
            <TennisUser />
          </div>
        </div>
        <div className="order-container">
          {paginatedOrders.length === 0 ? (
            <Card title={t('tennisOrder.noHistory')} style={{ marginBottom: 16 }} />
          ) : (paginatedOrders.map((order) => (
            <Card
              key={order.oid}
              title={`${t('tennisOrder.orderNumber')}: ${order.oid}`}
              style={{ marginBottom: 16 }}
              extra={
                <div>
                  <OrderTennisStatusTag status={order.status} />
                  <Switch
                    checked={visible[order.oid]}
                    onChange={() => handleToggleVisibility(order.oid)}
                    style={{ marginRight: 8 }}
                  />
                </div>
              }
            >
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Image
                  width={50}
                  height={50}
                  src={order.reservation_screenshot_path ? order.reservation_screenshot_path : "/uploads/default/table.png"}
                  alt={`Table ${order.tableNumber}`}
                  style={{ marginRight: 16 }}
                  preview={false}
                />
                {order.tableNumber && (
                  <div className="cartCornerMark">{order.tableNumber}</div>
                )}
                <div>
                  {t('tennisOrder.orderId')}: {order.id ?? order.oid}
                  <div>{t('tennisOrder.court')}: {order.court_name ?? '-'} {order.court_type ? `／ ${order.court_type}` : ''}</div>
                  <div>{t('tennisOrder.dateTime')}: {order.auction_date ? dayjs(order.auction_date).format('YYYY-MM-DD') : '-'}{order.time_range ? `　${order.time_range}` : ''}</div>
                </div>
              </div>
              <p>{t('order.totalPrice', { amount: Number(order.amount).toLocaleString() })}</p>
              <p>{t('tennisOrder.orderCreatedAt')}: {order.ctime}</p>
              {visible[order.oid] && (
                <div>
                  <p>{t('tennisOrder.paypayAccount')}: {order.paypay_account ?? '-'}</p>
                  <p>{t('tennisOrder.buyerEmail')}: {order.buyer_email ?? '-'}</p>
                  <p>{t('tennisOrder.paymentConfirmedAt')}: {order.payment_confirmed_at ?? '-'}</p>
                  <p>{t('tennisOrder.orderCreatedAt')}: {order.order_created_at ?? order.created_at ?? '-'}</p>
                  <p>{t('tennisOrder.auctionPrice')}: {Number(order.amount).toLocaleString()} 円</p>
                  <p>{t('tennisOrder.auctionStatus')}: {order.auction_status ?? '-'}</p>
                  <p>{t('tennisOrder.buyerName')}: {order.buyer_uname ?? '-'}</p>
                  <p>{t('tennisOrder.buyerPhone')}: {order.buyer_phoneNumber ?? '-'}</p>
                  <p>{t('tennisOrder.buyerMailAddress')}: {order.buyer_mailAddress ?? '-'}</p>
                  <p>{t('tennisOrder.buyerPaypayAccount')}: {order.buyer_paypay_account ?? '-'}</p>
                </div>
              )}
            </Card>
          )))}
        </div>
        <Pagination
          current={currentPage}
          pageSize={pageSize}
          total={orders.length}
          onChange={handlePageChange}
          style={{ textAlign: 'center', marginTop: 16 }}
        />
      </div>
    </div>
  );
};

export default TennisPurchaseHistory;
